import { useEffect, useState } from "react";
import { Save, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useContent, useContentState } from "../context/ContentContext";
import { adminApi, formatApiError } from "../context/AuthContext";
import { images } from "../config/siteConfig";
import { useSaveContent } from "./AdminLayout";
import { Card, Text, Area, ImageField, ObjectList, btnPrimary, btnGhost } from "./fields";

const phoneToHref = (display) => `tel:${display.replace(/[^\d+]/g, "").replace(/^0/, "+31")}`;
const phoneToWhatsapp = (display, text) =>
  `https://wa.me/${display.replace(/[^\d]/g, "").replace(/^0/, "31")}?text=${encodeURIComponent(text)}`;

export default function AdminTeksten() {
  const { site, pages, workArea } = useContent();
  const { reload } = useContentState();
  const { save, saving } = useSaveContent();
  const [s, setS] = useState(site);
  const [p, setP] = useState(pages);
  const [w, setW] = useState(workArea);
  const [waText, setWaText] = useState("Hoi! Ik heb een vraag over een klus. Ik stuur een foto mee.");

  useEffect(() => { setS(site); setP(pages); setW(workArea); }, [site, pages, workArea]);

  const setSite = (k) => (v) => setS((d) => ({ ...d, [k]: v }));
  const setPage = (page, k) => (v) => setP((d) => ({ ...d, [page]: { ...d[page], [k]: v } }));
  const setArea = (k) => (v) => setW((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const siteOut = { ...s, phoneHref: phoneToHref(s.phoneDisplay), whatsappHref: phoneToWhatsapp(s.phoneDisplay, waText) };
    const places = (w.places || []).map((pl) => ({
      ...pl,
      coords: Array.isArray(pl.coords) ? pl.coords : String(pl.coords || "").split(",").map((n) => parseFloat(n.trim())),
    })).filter((pl) => pl.name && pl.coords.length === 2 && pl.coords.every((n) => !Number.isNaN(n)));
    await save({ site: siteOut, pages: p, workArea: { ...w, radiusKm: Number(w.radiusKm) || 25, places } });
  };

  const resetAll = async () => {
    if (!window.confirm("Alle aangepaste teksten, diensten en projecten terugzetten naar de standaardversie?")) return;
    try {
      await adminApi.delete("/admin/content");
      await reload();
      toast.success("Standaardteksten hersteld");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const placesForEdit = (w.places || []).map((pl) => ({ ...pl, coords: Array.isArray(pl.coords) ? pl.coords.join(", ") : pl.coords }));

  return (
    <form onSubmit={submit} data-testid="admin-teksten-page" className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold uppercase tracking-wide text-[#111111]">Teksten & instellingen</h1>
          <p className="mt-1 text-sm text-[#555555]">Contactgegevens, homepage, Over mij, portfolio-intro en werkgebied.</p>
        </div>
        <button type="submit" data-testid="texts-save-top" disabled={saving} className={btnPrimary}><Save className="h-4 w-4" /> {saving ? "Opslaan..." : "Alles opslaan"}</button>
      </div>

      <Card title="Contactgegevens">
        <div className="grid gap-4 sm:grid-cols-2">
          <Text label="Bedrijfsnaam" value={s.name} onChange={setSite("name")} testid="site-name" />
          <Text label="Telefoonnummer (weergave)" value={s.phoneDisplay} onChange={setSite("phoneDisplay")} testid="site-phone" />
          <Text label="E-mailadres" value={s.email} onChange={setSite("email")} testid="site-email" />
          <Text label="Vestigingsplaats" value={s.location} onChange={setSite("location")} testid="site-location" />
          <Text label="Werkgebied (tekst)" value={s.workArea} onChange={setSite("workArea")} testid="site-workarea" />
          <Text label="WhatsApp-begintekst" value={waText} onChange={setWaText} testid="site-whatsapp-text" />
          <Text label="Adres (voor voorwaarden en facturen)" value={s.address} onChange={setSite("address")} testid="site-address" placeholder="Straat 1, 1716 AA Opmeer" />
          <Text label="KvK-nummer" value={s.kvk} onChange={setSite("kvk")} testid="site-kvk" />
          <Text label="Btw-nummer" value={s.btw} onChange={setSite("btw")} testid="site-btw" placeholder="NL000000000B01" />
        </div>
      </Card>

      <Card title="Homepage: hero">
        <div className="grid gap-4 sm:grid-cols-2">
          <Text label="Label boven de kop" value={s.heroLabel} onChange={setSite("heroLabel")} testid="hero-label" />
          <Text label="Lint op de foto" value={s.heroRibbon} onChange={setSite("heroRibbon")} testid="hero-ribbon" />
          <Text label="Kop regel 1" value={s.heroTitle1} onChange={setSite("heroTitle1")} testid="hero-title1" />
          <Text label="Kop regel 2 (crème)" value={s.heroTitle2} onChange={setSite("heroTitle2")} testid="hero-title2" />
        </div>
        <Area label="Introtekst onder de kop" value={s.heroSubtext} onChange={setSite("heroSubtext")} rows={3} testid="hero-subtext" />
        <ImageField label="Hero-foto" value={{ src: s.heroImage || images.hero.src, alt: images.hero.alt }} onChange={(img) => setSite("heroImage")(img.src)} testid="hero-image-upload" />
      </Card>

      <Card title="Homepage: teksten">
        <Text label="Klein label" value={p.home.introEyebrow} onChange={setPage("home", "introEyebrow")} />
        <Text label="Kop" value={p.home.introTitle} onChange={setPage("home", "introTitle")} testid="home-intro-title" />
        <Area label="Tekst" value={p.home.introText} onChange={setPage("home", "introText")} rows={4} />
        <div className="grid gap-4 sm:grid-cols-2">
          <Text label="Blok 1: kop" value={p.home.aboutTitle1} onChange={setPage("home", "aboutTitle1")} />
          <Text label="Blok 2: kop" value={p.home.aboutTitle2} onChange={setPage("home", "aboutTitle2")} />
          <Area label="Blok 1: tekst" value={p.home.aboutText1} onChange={setPage("home", "aboutText1")} rows={4} />
          <Area label="Blok 2: tekst" value={p.home.aboutText2} onChange={setPage("home", "aboutText2")} rows={4} />
          <Text label="Offertekaart: kop" value={p.home.ctaTitle} onChange={setPage("home", "ctaTitle")} />
          <Area label="Offertekaart: tekst" value={p.home.ctaText} onChange={setPage("home", "ctaText")} rows={2} />
        </div>
      </Card>

      <Card title="Over mij">
        <div className="grid gap-4 sm:grid-cols-2">
          <Text label="Paginatitel" value={p.overMij.title} onChange={setPage("overMij", "title")} testid="overmij-title" />
          <Text label="Kop" value={p.overMij.heading} onChange={setPage("overMij", "heading")} />
        </div>
        <Area label="Intro" value={p.overMij.intro} onChange={setPage("overMij", "intro")} rows={2} />
        <Area label="Alinea 1" value={p.overMij.text1} onChange={setPage("overMij", "text1")} rows={4} />
        <Area label="Alinea 2" value={p.overMij.text2} onChange={setPage("overMij", "text2")} rows={4} />
        <ImageField label="Foto" value={{ src: s.portraitImage || images.portrait.src, alt: images.portrait.alt }} onChange={(img) => setSite("portraitImage")(img.src)} testid="portrait-image-upload" />
        <ObjectList label="Feiten" value={p.overMij.facts} onChange={setPage("overMij", "facts")} testid="overmij-facts" empty={{ label: "", value: "" }} fields={[{ key: "label", label: "Label" }, { key: "value", label: "Waarde" }]} />
        <Text label="Kop werkwijze" value={p.overMij.stepsTitle} onChange={setPage("overMij", "stepsTitle")} />
        <ObjectList label="Werkwijze-stappen" value={p.overMij.steps} onChange={setPage("overMij", "steps")} testid="overmij-steps" empty={{ title: "", text: "" }} fields={[{ key: "title", label: "Titel" }, { key: "text", label: "Tekst", multiline: true }]} />
      </Card>

      <Card title="Portfolio & contact">
        <div className="grid gap-4 sm:grid-cols-2">
          <Text label="Portfolio: titel" value={p.portfolio.title} onChange={setPage("portfolio", "title")} />
          <Text label="Contact: titel" value={p.contact.title} onChange={setPage("contact", "title")} />
          <Area label="Portfolio: intro" value={p.portfolio.intro} onChange={setPage("portfolio", "intro")} rows={3} />
          <Area label="Contact: intro" value={p.contact.intro} onChange={setPage("contact", "intro")} rows={3} />
        </div>
      </Card>

      <Card title="Werkgebied (kaart)">
        <div className="grid gap-4 sm:grid-cols-2">
          <Text label="Straal in km" type="number" value={w.radiusKm} onChange={setArea("radiusKm")} testid="area-radius" />
        </div>
        <ObjectList
          label="Plaatsen (coördinaten als 'breedtegraad, lengtegraad')"
          value={placesForEdit}
          onChange={setArea("places")}
          testid="area-places"
          empty={{ name: "", coords: "52.70, 4.95" }}
          fields={[{ key: "name", label: "Plaatsnaam" }, { key: "coords", label: "Coördinaten" }]}
        />
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#DDD9D0] pt-6">
        <button type="button" data-testid="content-reset" onClick={resetAll} className={`${btnGhost} hover:border-red-600 hover:text-red-600`}><Trash2 className="h-4 w-4" /> Alles terug naar standaard</button>
        <div className="flex gap-2">
          <button type="button" onClick={() => { setS(site); setP(pages); setW(workArea); }} className={btnGhost}><RotateCcw className="h-4 w-4" /> Herstel</button>
          <button type="submit" data-testid="texts-save" disabled={saving} className={btnPrimary}><Save className="h-4 w-4" /> {saving ? "Opslaan..." : "Alles opslaan"}</button>
        </div>
      </div>
    </form>
  );
}
